/* eslint-disable import/prefer-default-export */
import lod_ from "lodash";
import { socket } from "redux-react/middleware/ws";
import { useLanguages } from "./useLanguages";
import { useEffect, useState } from "react";

/**
 * Translation Actions Hook
 *
 * This hook provides functionality for translating content from a source language to multiple target languages.
 * It integrates with WebSocket communication for real-time translation processing and Redux for state management.
 *
 * TRANSLATION PROCESS OVERVIEW:
 * 1. User initiates translation by selecting a source language and content
 * 2. Hook validates the content against a provided Joi schema
 * 3. Socket emits translation request to backend with the dataset
 * 4. Backend processes translation and sends progress updates via snackbar messages with loading progression
 * 5. Upon completion, backend emits 'translate_action_v2_update' event with results
 * 6. Hook triggers callback with translated content for each language
 * 7. UI updates with translated data
 *
 * USAGE EXAMPLE:
 *
 * import { useTranslateActions } from 'hooks/useTranslateActions';
 * import { useLanguages } from 'hooks/useLanguages';
 * import Joi from 'joi';
 *
 * const MyComponent = () => {
 *   const { translateV2 } = useTranslateActions();
 *   const { languages } = useLanguages();
 *   const [translations, setTranslations] = useState({});
 *
 *   // Define a Joi schema for validation
 *   const translationSchema = Joi.object().pattern(
 *     Joi.string().trim().required().description("Language code"),
 *     Joi.string().trim().required().description("Translated text").min(1)
 *   );
 *
 *   const translationSchemaString = `Joi.object().pattern(
 *     Joi.string().trim().required().description("Language code"),
 *     Joi.string().trim().required().description("Translated text").min(1)
 *   )`;
 *
 *   const handleTranslate = (sourceLanguageCode) => {
 *     const sourceText = translations[sourceLanguageCode] || "Text to translate";
 *
 *     // Prepare the entry object with source language text
 *     const entryObject = {
 *       [sourceLanguageCode]: sourceText
 *     };
 *
 *     // Call translateV2 with all required parameters
 *     translateV2(
 *       "my-translation-code",              // unique code for this translation request
 *       sourceLanguageCode,                 // e.g., "en"
 *       entryObject,                        // object containing source text
 *       translationSchema,                  // Joi schema for validation
 *       translationSchemaString,            // String representation of schema
 *       ({ code, result, success }) => {    // Callback function
 *         if (success) {
 *           // result contains all translated texts with language codes as keys
 *           // e.g., { en: "...", fr: "...", es: "...", de: "..." }
 *           setTranslations(prev => ({
 *             ...prev,
 *             ...result
 *           }));
 *         }
 *       }
 *     );
 *   };
 *
 *   return (
 *     <button onClick={() => handleTranslate('en')}>
 *       Translate from English
 *     </button>
 *   );
 * };
 *
 * BACKEND SNACKBAR PROGRESS:
 * The backend automatically sends snackbar messages with loading progression:
 * - Shows initial loading message when translation starts
 * - Updates progress for each language being translated
 * - Displays completion message when finished
 * - Sends error messages if translation fails
 * These messages are dispatched to Redux snackBarReducer automatically.
 *
 * @returns {Object} - { translateV2 } - function to initiate translations
 */
export const useTranslateActions = () => {
	const { languages } = useLanguages();

	// Map to store callbacks for each translation request by code
	// When backend sends results via 'translate_action_v2_update', we look up the callback here
	const [callbacks, setCallbacks] = useState(new Map());

	/**
	 * Batch translate multiple datasets at once
	 * This is used internally to send multiple translation requests in one WebSocket emission
	 * @param {Array} dataset - Array of translation request objects
	 */
	const translateV2Dataset = (schema, schemaString, dataset, endCallback = () => {}) => {
		const languagesCode = lod_.map(languages, "code");

		// Store the callbacks in state so we can retrieve them later when results come back
		// We store them before sending to socket to avoid race conditions
		setCallbacks(prevCallback => {
			const validDatasets = [];

			dataset.forEach(data => {
				/**
				 * Calculate target languages
				 * We translate TO all languages except the source language
				 */
				const targetLanguages = lod_.filter(
					languagesCode,
					code => code?.toLowerCase() !== data.sourceLanguage?.toLowerCase()
				);
				if (lod_.isEmpty(data.targetLanguages) || lod_.isNil(data.targetLanguages)) {
					data.targetLanguages = targetLanguages;
				}

				/**
				 * Validation step: Entry object must match the schema structure
				 * This ensures data integrity before sending to backend
				 */
				const { error: validationError } = schema.validate(data.entryObject, {
					abortEarly: false
				});

				if (validationError) {
					data.callback({
						success: false,
						error: "Validation error: " + validationError.message
					});
					return;
				}

				// Each request has a unique 'code' identifier
				// Store the callback function for this code
				prevCallback.set(data.code, data.callback);
				// Remove callback from dataset before sending to backend
				// (backend doesn't need the function, only the data)
				delete data.callback;

				data.schema = schemaString;

				validDatasets.push(data);
			});

			// Emit the WebSocket event to backend
			// Backend will process this and send back 'translate_action_v2_update' events
			// with the translation results for each language in the dataset
			socket.emit(
				"translate_action_v2",
				{
					dataset: validDatasets
				},
				endCallback
			);

			return prevCallback;
		});
	};

	/**
	 * Translate an entry object, from a source language to target languages
	 *
	 * PROCESS FLOW:
	 * 1. Validates the source content against the Joi schema
	 * 2. Calculates all target languages (all languages except the source)
	 * 3. Prepares translation request with source content and schema
	 * 4. Sends WebSocket event to backend with the request
	 * 5. Backend processes translation for each target language
	 * 6. Backend sends progress updates via snackbar messages with loading indication
	 * 7. When complete, backend emits 'translate_action_v2_update' event
	 * 8. This hook catches that event and calls the callback with results
	 *
	 * EXAMPLE:
	 * const sourceText = "Hello, how are you?";
	 * translateV2(
	 *   "answer_title_42",           // Unique identifier for this translation
	 *   "en",                        // Translate FROM English
	 *   { en: sourceText },          // Object with source language content
	 *   myJoiSchema,                 // Joi schema for validation
	 *   myJoiSchemaString,           // String representation of schema
	 *   (result) => {                // Callback when translation is done
	 *     console.log(result);       // { en: "Hello, how are you?", fr: "Bonjour, comment allez-vous?", ... }
	 *   }
	 * );
	 *
	 * @param {string} code - Unique identifier for this translation action (used to match requests with responses)
	 * @param {string} sourceLanguage - Language code of the source language (e.g., "en", "fr", "es")
	 * @param {Object} entryObject - Object containing the content to translate (e.g., { en: "Hello" })
	 * @param {Joi.Schema} schema - Joi schema object for validation
	 * @param {string} schemaString - String representation of the Joi schema (for backend execution)
	 * @param {Function} cb - Callback executed when translation completes.
	 *                        Receives object with { code, result, success }
	 *                        result contains translations: { en: "...", fr: "...", es: "...", ... }
	 */
	const translateV2 = (code, sourceLanguage, entryObject, schema, schemaString, cb = () => {}) => {
		// Prepare the translation request dataset
		const dataset = [
			{
				code, // Unique identifier to match request with response
				sourceLanguage, // Language we're translating FROM
				entryObject, // The content to translate
				callback: cb // Callback to execute when done (will be removed before sending)
			}
		];

		// Send to backend via WebSocket
		// Backend will:
		// 1. Process the translation request
		// 2. Send snackbar messages with loading progression
		// 3. Translate to each target language
		// 4. Emit 'translate_action_v2_update' event when complete
		translateV2Dataset(schema, schemaString, dataset);
	};

	/**
	 * Handle response from backend when translation is complete
	 *
	 * BACKEND PROCESS:
	 * 1. Backend receives 'translate_action_v2' event from this hook
	 * 2. Backend extracts the source text from entryObject
	 * 3. Backend validates content against the provided schema
	 * 4. Backend generates snackbar messages for loading progression:
	 *    - "Translating content..." (initial)
	 *    - "Translated to French (1/3)" (progress)
	 *    - "Translated to Spanish (2/3)" (progress)
	 *    - "Translation completed successfully (3/3)" (final)
	 *    These messages are sent to Redux snackBarReducer for display
	 * 5. Backend calls translation API for each target language
	 * 6. Backend sends back 'translate_action_v2_update' event with results
	 *
	 * THIS FUNCTION:
	 * - Listens for the 'translate_action_v2_update' WebSocket event
	 * - Retrieves the callback associated with this translation (by code)
	 * - Calls the callback with the translation results
	 * - The callback updates the parent component's state with translated content
	 *
	 * @param {Object} param0 - Response object from backend
	 * @param {boolean} param0.success - Whether translation was successful
	 * @param {Object} param0.result - Translated content { en: "...", fr: "...", etc }
	 * @param {string} param0.code - Unique identifier matching the original request
	 */
	const translationUpdate = ({ success, result, code }) => {
		// Look up the callback that was stored when we sent the translation request
		const callback = callbacks.get(code);

		// If callback exists, call it with the translation results
		// The callback updates the parent component's state
		if (!lod_.isNil(callback)) {
			callback(result);
		}
	};

	useEffect(() => {
		// Register listener for translation completion events
		// When backend sends 'translate_action_v2_update', this callback is executed
		socket.on("translate_action_v2_update", translationUpdate);

		// Cleanup: remove listener when callbacks change or component unmounts
		// This prevents duplicate listeners and memory leaks
		return () => {
			socket.off("translate_action_v2_update", translationUpdate);
		};
	}, [callbacks]);

	useEffect(() => {
		// Initialize callbacks map on mount
		// This ensures a clean state when the hook is initialized
		setCallbacks(new Map());
	}, []);

	return {
		translateV2,
		translateV2Dataset
	};
};
